// LocationAutocomplete.js
// Uses OpenStreetMap Nominatim — no API key required
// Drop this in src/components/LocationAutocomplete.js

import React, { useState, useRef, useEffect, useCallback } from 'react';

export default function LocationAutocomplete({
  value,
  onChange,
  placeholder = 'e.g. Tracy, CA',
  style = {},
  inputStyle = {},
}) {
  const [query, setQuery]       = useState(value || '');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const debounceRef             = useRef(null);
  const wrapRef                 = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync if parent value changes
  useEffect(() => { if (value !== query) setQuery(value || ''); }, [value]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=6&countrycodes=us`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'Vyblo/1.0' } }
      );
      const data = await res.json();
      // Format nicely: "City, State" or "City, State ZIP"
      const formatted = data.map(item => {
        const a = item.address || {};
        const parts = [
          a.city || a.town || a.village || a.county,
          a.state,
        ].filter(Boolean);
        return {
          display: parts.join(', '),
          full: item.display_name,
        };
      }).filter((v, i, arr) => v.display && arr.findIndex(x => x.display === v.display) === i); // dedupe
      setResults(formatted);
      setOpen(formatted.length > 0);
    } catch {}
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v); // keep parent in sync as they type
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const handleSelect = (item) => {
    setQuery(item.display);
    onChange(item.display);
    setResults([]);
    setOpen(false);
  };

  const baseInputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.08)',
    border: '1.5px solid rgba(255,255,255,0.18)',
    borderRadius: 10,
    padding: '0.75rem 1rem',
    color: 'white',
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'DM Sans, sans-serif',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    ...inputStyle,
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem', pointerEvents: 'none' }}>📍</span>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(13,148,136,0.6)'; if (results.length > 0) setOpen(true); }}
          onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.18)'}
          placeholder={placeholder}
          autoComplete="off"
          style={{ ...baseInputStyle, paddingLeft: '2.25rem', paddingRight: loading ? '2.5rem' : '0.875rem' }}
        />
        {loading && (
          <div style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(13,148,136,0.4)', borderTopColor: '#0d9488', animation: 'spin 0.7s linear infinite' }} />
        )}
      </div>

      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'rgba(6,18,40,0.98)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, zIndex: 500, boxShadow: '0 12px 40px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
          {results.map((item, i) => (
            <div key={i} onMouseDown={() => handleSelect(item)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.75rem 1rem', cursor: 'pointer', borderBottom: i < results.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none', transition: 'background 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(13,148,136,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontSize: '0.85rem', flexShrink: 0, color: 'rgba(255,255,255,0.35)' }}>📍</span>
              <div>
                <p style={{ fontSize: '0.855rem', fontWeight: 600, color: 'white', marginBottom: '0.1rem' }}>{item.display}</p>
                <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>{item.full}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
